<script setup>
/* global axios */ // GK-HUB: uses Chatwoot global axios (has auth headers)
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';

import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
// GK-HUB: shared branded channel SVG + attachments renderer
import BrandedChannelSvg from 'next/icon/BrandedChannelSvg.vue';
import IspMetricCard from './components/IspMetricCard.vue';
import { useIspReport } from './composables/useIspReport';
import { formatTime } from './composables/ispFormatters';
import { exportCsv } from './composables/useIspExport';
// GK-HUB: Import role helper for team filter visibility
import { getUserRole } from 'dashboard/helper/permissionsHelper';
import './styles/isp-filters.css';

const { t } = useI18n();
const store = useStore();

// GK-HUB: Removed showDeptFilter/departments/selectedDeptId — using team filter instead
const {
  customDateRange,
  selectedDateRange,
  data,
  error,
  retry,
  onDateRangeChange,
  onFilterChange,
  filteredAgents,
  filters,
} = useIspReport({
  getter: 'ispReports/getConversas',
  action: 'ispReports/fetchConversas',
  filters: {
    agentId: '',
    motivo: '',
    status: '',
    iaFilter: '',
    channelType: '',
    search: '',
    page: '1',
    teamId: '',
  },
  initActions: ['agents/get'],
  // GK-HUB: ao filtrar por status=resolved, filtra pela DATA DE RESOLUCAO (updated_at,
  // via date_field=resolved_at) em vez da data de abertura. Conversas abertas antes do
  // intervalo mas resolvidas dentro dele passam a aparecer. (#27)
  paramsTransform: params =>
    params.status === 'resolved'
      ? { ...params, dateField: 'resolved_at' }
      : params,
});

// Motivos list for filter dropdown
const motivosList = computed(() => store.getters['ispReports/getMotivosList']);
store.dispatch('ispReports/fetchMotivosList');

// GK-HUB: Teams list + showTeamFilter (admin/manager only, supervisor auto-scoped by dept on backend)
const teams = computed(() => store.getters['teams/getTeams']);
store.dispatch('teams/get');
const currentUser = computed(() => store.getters.getCurrentUser);

const isFetching = computed(() => data.value?.isFetching || false);
const reportError = computed(() => error.value);
const conversations = computed(() => data.value?.data?.conversations || []);
const summary = computed(() => data.value?.data?.summary || {});
const pagination = computed(
  () =>
    data.value?.data?.pagination || {
      current_page: 1,
      total_pages: 1,
      total_count: 0,
      per_page: 25,
    }
);

// Pagination helpers
const showingFrom = computed(() => {
  const p = pagination.value;
  if (p.total_count === 0) return 0;
  return (p.current_page - 1) * p.per_page + 1;
});
const showingTo = computed(() => {
  const p = pagination.value;
  return Math.min(p.current_page * p.per_page, p.total_count);
});

const goToPage = page => {
  if (page < 1 || page > pagination.value.total_pages) return;
  onFilterChange('page', String(page));
};

// Reset to page 1 when any filter changes (except page itself)
const onFilterChangeWithReset = (key, value) => {
  if (key !== 'page') {
    filters.page.value = '1';
  }
  onFilterChange(key, value);
};

// Search debounce
let searchTimeout = null;
const searchInput = ref(filters.search.value);
const onSearchInput = e => {
  searchInput.value = e.target.value;
  clearTimeout(searchTimeout);
  searchTimeout = setTimeout(() => {
    onFilterChangeWithReset('search', searchInput.value);
  }, 400);
};

// Slide-over state
const selectedConversationId = ref(null);
const detailData = computed(
  () => store.getters['ispReports/getConversasDetail']
);
const detailFetching = computed(() => detailData.value?.isFetching || false);
const detailConv = computed(() => detailData.value?.data?.conversation || null);
const detailMessages = computed(() => detailData.value?.data?.messages || []);
// GK-HUB: Activities for timeline panel
const detailActivities = computed(
  () => detailData.value?.data?.activities || []
);

const openSlideOver = convId => {
  selectedConversationId.value = convId;
  store.dispatch('ispReports/fetchConversasDetail', convId);
};

const closeSlideOver = () => {
  selectedConversationId.value = null;
};

// Close on ESC key
const onKeydown = e => {
  if (e.key === 'Escape') closeSlideOver();
};
watch(selectedConversationId, val => {
  if (val) {
    document.addEventListener('keydown', onKeydown);
  } else {
    document.removeEventListener('keydown', onKeydown);
  }
});

// Current account ID for conversation URL + team filter visibility
const currentAccountId = computed(() => store.getters.getCurrentAccountId);
// GK-HUB: showTeamFilter — admin/manager only
const showTeamFilter = computed(() =>
  ['administrator', 'manager'].includes(
    getUserRole(currentUser.value, currentAccountId.value)
  )
);
const conversationUrl = computed(() => {
  if (!detailConv.value) return '#';
  return `/app/accounts/${currentAccountId.value}/conversations/${detailConv.value.display_id}`;
});

// Status badge
const statusBadge = status => {
  const map = {
    resolved: {
      label: t('ISP_REPORTS.CONVERSAS.STATUS_RESOLVED'),
      class: 'bg-n-teal-3 text-n-teal-11',
    },
    open: {
      label: t('ISP_REPORTS.CONVERSAS.STATUS_OPEN'),
      class: 'bg-n-blue-3 text-n-blue-11',
    },
    pending: {
      label: t('ISP_REPORTS.CONVERSAS.STATUS_PENDING'),
      class:
        'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    },
  };
  return (
    map[status] || { label: status, class: 'bg-n-solid-3 text-n-slate-10' }
  );
};

// GK-HUB: Channel badge — Tailwind classes per channel_type for colored pill
const channelBadgeClasses = type => {
  const map = {
    'Channel::Whatsapp': 'bg-green-500/10 text-green-600',
    'Channel::WebWidget': 'bg-n-blue-3 text-n-blue-11',
    'Channel::Api': 'bg-n-blue-3 text-n-blue-11',
    'Channel::FacebookPage': 'bg-blue-600/10 text-blue-600',
    'Channel::Email': 'bg-n-blue-3 text-n-blue-11',
    'Channel::Telegram': 'bg-cyan-500/10 text-cyan-600',
    'Channel::Sms': 'bg-amber-500/10 text-amber-600',
    'Channel::Line': 'bg-green-600/10 text-green-700',
    'Channel::TwitterProfile': 'bg-n-slate-3 text-n-slate-11',
    'Channel::Instagram': 'bg-pink-500/10 text-pink-600',
    'Channel::VoiceFreepbx': 'bg-n-teal-9/10 text-n-teal-11',
  };
  return map[type] || 'bg-n-slate-3 text-n-slate-11';
};

// GK-HUB: Normalize channel_type to BrandedChannelSvg channel name
const channelBrandedName = type => {
  const map = {
    'Channel::Whatsapp': 'whatsapp',
    'Channel::Instagram': 'instagram',
    'Channel::Telegram': 'telegram',
    'Channel::FacebookPage': 'messenger',
    'Channel::TwitterProfile': 'twitter',
    'Channel::Line': 'line',
    'Channel::Email': 'email',
    'Channel::WebWidget': 'website',
    'Channel::VoiceFreepbx': 'phone',
  };
  return map[type] || null;
};
// GK-HUB: end channel badge helpers

// GK-HUB: Label helpers — handle both string (legacy) and {name, color} (new format)
const labelName = label =>
  typeof label === 'string' ? label : label?.name || '';
const labelColor = label =>
  typeof label === 'string' ? '#1f93ff' : label?.color || '#1f93ff';
const labelPillStyle = label => {
  const color = labelColor(label);
  return {
    backgroundColor: color + '1a', // ~10% opacity hex
    borderColor: color + '33', // ~20% opacity hex
    color: color,
  };
};
// GK-HUB: end label helpers

// CSAT stars
const csatStars = rating => {
  if (!rating) return '-';
  return (
    '\u2605'.repeat(Math.round(rating)) +
    '\u2606'.repeat(5 - Math.round(rating))
  );
};

// Check if row is IA resolved
const isIaRow = conv => conv.resolved_by === 'ia';

// Message bubble helpers (slide-over)
const msgSenderClass = msg => {
  if (msg.is_private) return 'text-amber-500';
  if (msg.sender_type === 'AgentBot') return 'text-purple-400';
  if (msg.message_type === 'incoming') return 'text-n-teal-11';
  return 'text-n-blue-11';
};
const msgIcon = msg => {
  if (msg.is_private) return 'i-lucide-lock';
  if (msg.sender_type === 'AgentBot') return 'i-lucide-bot';
  if (msg.message_type === 'incoming') return 'i-lucide-user';
  return 'i-lucide-headset';
};

// GK-HUB: Timeline builder — combines conv start + activities + resolved into unified timeline
const relativeTime = (eventDate, baseDate) => {
  const diff = Math.max(0, Math.round((eventDate - baseDate) / 60000));
  if (diff < 60) return `+${diff}m`;
  const h = Math.floor(diff / 60);
  const m = diff % 60;
  return `+${h}h ${m}m`;
};

const timelineIcon = content => {
  if (!content)
    return {
      icon: 'i-lucide-activity',
      dotBg: 'bg-n-solid-3',
      iconColor: 'text-n-slate-9',
    };
  const c = content.toLowerCase();
  if (c.includes('assigned') || c.includes('atribuí'))
    return {
      icon: 'i-lucide-user-check',
      dotBg: 'bg-n-blue-9/20',
      iconColor: 'text-n-blue-11',
    };
  if (c.includes('resolved') || c.includes('resolvid'))
    return {
      icon: 'i-lucide-check-circle',
      dotBg: 'bg-n-teal-9/20',
      iconColor: 'text-n-teal-11',
    };
  if (c.includes('reopened') || c.includes('reabr'))
    return {
      icon: 'i-lucide-refresh-cw',
      dotBg: 'bg-amber-500/20',
      iconColor: 'text-amber-500',
    };
  if (c.includes('priority') || c.includes('priorid'))
    return {
      icon: 'i-lucide-flag',
      dotBg: 'bg-amber-500/20',
      iconColor: 'text-amber-500',
    };
  if (c.includes('team') || c.includes('equipe') || c.includes('transfer'))
    return {
      icon: 'i-lucide-users',
      dotBg: 'bg-n-blue-9/20',
      iconColor: 'text-n-blue-11',
    };
  if (
    c.includes('bot') ||
    c.includes('ia') ||
    c.includes('diagnóstic') ||
    c.includes('ixc') ||
    c.includes('automátic')
  )
    return {
      icon: 'i-lucide-bot',
      dotBg: 'bg-purple-500/20',
      iconColor: 'text-purple-400',
    };
  if (c.includes('label') || c.includes('tag'))
    return {
      icon: 'i-lucide-tag',
      dotBg: 'bg-n-solid-3',
      iconColor: 'text-n-slate-9',
    };
  return {
    icon: 'i-lucide-activity',
    dotBg: 'bg-n-solid-3',
    iconColor: 'text-n-slate-9',
  };
};

const timeline = computed(() => {
  if (!detailConv.value) return [];
  const events = [];
  const baseDate = new Date(detailConv.value.created_at);

  // Event: Conversa iniciada
  events.push({
    ...timelineIcon('started'),
    icon: 'i-lucide-message-circle',
    dotBg: 'bg-n-teal-9/20',
    iconColor: 'text-n-teal-11',
    label: t('ISP_REPORTS.CONVERSAS.DETAIL_CONV_STARTED'),
    description: detailConv.value.channel_name
      ? t('ISP_REPORTS.CONVERSAS.DETAIL_VIA') +
        ' ' +
        detailConv.value.channel_name
      : '',
    time: baseDate,
    relativeTime: '+0m',
  });

  // Activities from backend
  detailActivities.value.forEach(act => {
    const actDate = new Date(act.created_at);
    const icons = timelineIcon(act.content);
    events.push({
      ...icons,
      label: act.content?.split('\n')[0]?.substring(0, 80) || '-',
      description: '',
      time: actDate,
      relativeTime: relativeTime(actDate, baseDate),
    });
  });

  // Event: Conversa resolvida
  if (detailConv.value.resolved_at) {
    const resolvedDate = new Date(detailConv.value.resolved_at);
    events.push({
      icon: 'i-lucide-check-circle',
      dotBg: 'bg-n-teal-9/20',
      iconColor: 'text-n-teal-11',
      label: t('ISP_REPORTS.CONVERSAS.DETAIL_CONV_RESOLVED'),
      description: '',
      time: resolvedDate,
      relativeTime: relativeTime(resolvedDate, baseDate),
    });
  }

  return events;
});

const totalDuration = computed(() => {
  if (!detailConv.value) return 0;
  return detailConv.value.duration_seconds || 0;
});
// GK-HUB: end timeline builder

// GK-HUB: PDF export for conversation slide-over
const exportingPdf = ref(false);
const exportPdf = async () => {
  if (!selectedConversationId.value) return;
  exportingPdf.value = true;
  try {
    const response = await axios.get(
      `/api/v2/accounts/${currentAccountId.value}/isp_reports/export_pdf`,
      {
        params: { conversation_id: selectedConversationId.value },
        responseType: 'blob',
      }
    );
    const url = URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = url;
    const convId = detailConv.value?.display_id || selectedConversationId.value;
    a.download = `GKC-${convId}-${new Date().toISOString().slice(0, 10)}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  } catch {
    // silently ignore — rare failure
  } finally {
    exportingPdf.value = false;
  }
};
// GK-HUB: end PDF export

// CSV export
const handleExport = () => {
  const rows = conversations.value;
  const header = '#,Canal,Cliente,Agente,Motivo,TMA,CSAT,Status\n';
  const mapFn = c =>
    [
      c.display_id,
      c.channel_name || '',
      c.contact_name || '',
      c.agent_name || 'GK.IA',
      c.motivo || '',
      c.duration_seconds ? formatTime(c.duration_seconds) : '',
      c.csat_rating || '',
      c.status || '',
    ]
      .map(v => `"${String(v).replace(/"/g, '""')}"`)
      .join(',');
  exportCsv(rows, header, mapFn, 'conversas-isp.csv');
};

const formatTimeOnly = iso => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

// GK-HUB: Parse CSAT message options and mark which one the customer selected.
// content_attributes.items = [{value: 'csat_5', title: '🤩 Ótimo'}, ...]
// content_attributes.submitted_values.csat_survey_response.rating = 5
const csatMessageOptions = msg => {
  const attrs = msg.content_attributes || {};
  const items = attrs.items || [];
  const submittedRating = attrs.submitted_values?.csat_survey_response?.rating;
  return items.map(it => {
    const match = String(it.value || '').match(/(\d+)/);
    const itemRating = match ? Number(match[1]) : null;
    return {
      value: it.value,
      title: it.title,
      selected: itemRating != null && itemRating === submittedRating,
    };
  });
};

const csatMessageSubmitted = msg =>
  !!msg.content_attributes?.submitted_values?.csat_survey_response?.rating;

const formatDate = iso => {
  if (!iso) return '-';
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

// GK-HUB: Chat bubble style for WhatsApp-like layout
const bubbleStyle = msg => {
  if (msg.is_private) return 'chat-bubble chat-bubble--private';
  if (msg.message_type === 'incoming') return 'chat-bubble chat-bubble--client';
  if (msg.sender_type === 'AgentBot') return 'chat-bubble chat-bubble--bot';
  return 'chat-bubble chat-bubble--agent';
};
</script>

<template>
  <div class="p-6">
    <!-- GK-HUB: Standardized header (no filters in header) -->
    <div class="mb-4">
      <h1 class="text-xl font-semibold text-n-slate-12">
        {{ t('ISP_REPORTS.CONVERSAS.HEADER') }}
      </h1>
      <p class="text-[13px] text-n-slate-10 mt-0.5">
        {{ t('ISP_REPORTS.CONVERSAS.SUBTITLE') }}
      </p>
    </div>

    <!-- GK-HUB: Standardized filter bar (DatePicker + Team filter) -->
    <div class="flex items-center gap-3 mb-6 flex-wrap">
      <WootDatePicker
        v-model:date-range="customDateRange"
        v-model:range-type="selectedDateRange"
        @date-range-changed="onDateRangeChange"
      />
      <!-- Team filter (admin/manager only) -->
      <div v-if="showTeamFilter" class="isp-select-wrap">
        <select
          :value="filters.teamId.value"
          class="isp-select"
          @change="onFilterChangeWithReset('teamId', $event.target.value)"
        >
          <option value="">
            {{ t('ISP_REPORTS.PAINEL.TODOS_TIMES') }}
          </option>
          <option v-for="team in teams" :key="team.id" :value="team.id">
            {{ team.name }}
          </option>
        </select>
        <span class="isp-select-arrow i-lucide-chevron-down" />
      </div>
    </div>

    <!-- Filters bar -->
    <div
      class="mb-5 p-3 bg-n-solid-2 border border-n-weak rounded-[10px] space-y-2"
    >
      <!-- Search row -->
      <div class="relative">
        <span
          class="i-lucide-search absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-n-slate-8"
        />
        <input
          :value="searchInput"
          :placeholder="t('ISP_REPORTS.CONVERSAS.SEARCH_PLACEHOLDER')"
          class="w-full text-[12px] text-n-slate-11 bg-n-solid-3 border border-n-weak rounded-lg pl-8 pr-3 py-1.5 outline-none focus:border-n-brand transition-colors placeholder:text-n-slate-8"
          @input="onSearchInput"
        />
      </div>
      <!-- Filter dropdowns row -->
      <div class="flex flex-wrap items-center gap-2">
        <!-- Agent filter -->
        <div class="isp-select-wrap">
          <select
            :value="filters.agentId.value"
            class="isp-select"
            @change="onFilterChangeWithReset('agentId', $event.target.value)"
          >
            <option value="">
              {{ t('ISP_REPORTS.CONVERSAS.FILTER_ALL_AGENTS') }}
            </option>
            <option
              v-for="agent in filteredAgents"
              :key="agent.id"
              :value="agent.id"
            >
              {{ agent.name }}
            </option>
          </select>
          <span class="isp-select-arrow i-lucide-chevron-down" />
        </div>
        <!-- Motivo filter -->
        <div class="isp-select-wrap">
          <select
            :value="filters.motivo.value"
            class="isp-select"
            @change="onFilterChangeWithReset('motivo', $event.target.value)"
          >
            <option value="">
              {{ t('ISP_REPORTS.CONVERSAS.FILTER_ALL_REASONS') }}
            </option>
            <option v-for="m in motivosList" :key="m" :value="m">
              {{ m }}
            </option>
          </select>
          <span class="isp-select-arrow i-lucide-chevron-down" />
        </div>
        <!-- Status filter -->
        <div class="isp-select-wrap">
          <select
            :value="filters.status.value"
            class="isp-select"
            @change="onFilterChangeWithReset('status', $event.target.value)"
          >
            <option value="">
              {{ t('ISP_REPORTS.CONVERSAS.FILTER_ALL_STATUS') }}
            </option>
            <option value="resolved">
              {{ t('ISP_REPORTS.CONVERSAS.STATUS_RESOLVED') }}
            </option>
            <option value="open">
              {{ t('ISP_REPORTS.CONVERSAS.STATUS_OPEN') }}
            </option>
            <option value="pending">
              {{ t('ISP_REPORTS.CONVERSAS.STATUS_PENDING') }}
            </option>
          </select>
          <span class="isp-select-arrow i-lucide-chevron-down" />
        </div>
        <!-- IA filter -->
        <div class="isp-select-wrap">
          <select
            :value="filters.iaFilter.value"
            class="isp-select"
            @change="onFilterChangeWithReset('iaFilter', $event.target.value)"
          >
            <option value="">
              {{ t('ISP_REPORTS.CONVERSAS.FILTER_IA_ALL') }}
            </option>
            <option value="ia_resolved">
              {{ t('ISP_REPORTS.CONVERSAS.FILTER_IA_RESOLVED') }}
            </option>
            <option value="agent_resolved">
              {{ t('ISP_REPORTS.CONVERSAS.FILTER_IA_TRANSFERRED') }}
            </option>
          </select>
          <span class="isp-select-arrow i-lucide-chevron-down" />
        </div>
      </div>
    </div>

    <!-- Loading -->
    <woot-loading-state
      v-if="isFetching && !data.data"
      :message="t('ISP_REPORTS.LOADING')"
      class="h-64"
    />

    <!-- Error -->
    <div
      v-else-if="reportError && !data.data"
      class="flex flex-col items-center justify-center h-64 gap-3"
    >
      <h3 class="text-base font-semibold text-n-slate-12">
        {{ t('ISP_REPORTS.ERROR_TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-10">
        {{ t('ISP_REPORTS.ERROR_MESSAGE') }}
      </p>
      <button
        class="bg-n-brand text-white rounded-md text-sm font-medium px-4 py-2 cursor-pointer"
        @click="retry"
      >
        {{ t('ISP_REPORTS.RETRY') }}
      </button>
    </div>

    <template v-if="data.data">
      <!-- KPI Cards -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <IspMetricCard
          :label="t('ISP_REPORTS.CONVERSAS.TOTAL')"
          :value="summary.total || 0"
          :loading="isFetching"
        />
        <IspMetricCard
          :label="t('ISP_REPORTS.CONVERSAS.RESOLVED')"
          :value="summary.resolved || 0"
          value-color="text-n-teal-11"
          :loading="isFetching"
        />
        <IspMetricCard
          :label="t('ISP_REPORTS.CONVERSAS.IA_RESOLVED')"
          :value="summary.ia_resolved || 0"
          value-color="text-purple-400"
          :loading="isFetching"
        />
        <IspMetricCard
          :label="t('ISP_REPORTS.CONVERSAS.AVG_TMA')"
          :value="formatTime(summary.avg_tma_seconds || 0)"
          value-color="text-n-blue-11"
          :loading="isFetching"
        />
      </div>

      <!-- Table container -->
      <div
        class="bg-n-solid-2 border border-n-weak rounded-[10px] overflow-hidden"
      >
        <div class="overflow-x-auto">
          <table class="w-full text-[13px] min-w-[900px]">
            <thead>
              <tr class="border-b border-n-weak bg-n-solid-3">
                <th
                  class="text-left font-semibold text-n-slate-10 px-3 py-3 w-16"
                >
                  {{ t('ISP_REPORTS.CONVERSAS.COL_ID') }}
                </th>
                <th
                  class="text-left font-semibold text-n-slate-10 px-3 py-3 whitespace-nowrap"
                >
                  {{ t('ISP_REPORTS.CONVERSAS.COL_CHANNEL') }}
                </th>
                <th class="text-left font-semibold text-n-slate-10 px-3 py-3">
                  {{ t('ISP_REPORTS.CONVERSAS.COL_CLIENT') }}
                </th>
                <th class="text-left font-semibold text-n-slate-10 px-3 py-3">
                  {{ t('ISP_REPORTS.CONVERSAS.COL_AGENT') }}
                </th>
                <th class="text-left font-semibold text-n-slate-10 px-3 py-3">
                  {{ t('ISP_REPORTS.CONVERSAS.COL_REASON') }}
                </th>
                <th class="text-left font-semibold text-n-slate-10 px-3 py-3">
                  {{ t('ISP_REPORTS.CONVERSAS.COL_LABELS') }}
                </th>
                <th
                  class="text-right font-semibold text-n-slate-10 px-3 py-3 whitespace-nowrap"
                >
                  {{ t('ISP_REPORTS.CONVERSAS.COL_TMA') }}
                </th>
                <th class="text-center font-semibold text-n-slate-10 px-3 py-3">
                  {{ t('ISP_REPORTS.CONVERSAS.COL_CSAT') }}
                </th>
                <th class="text-center font-semibold text-n-slate-10 px-3 py-3">
                  {{ t('ISP_REPORTS.CONVERSAS.COL_STATUS') }}
                </th>
              </tr>
            </thead>
            <!-- eslint-disable vue/no-bare-strings-in-template -->
            <tbody>
              <tr
                v-for="conv in conversations"
                :key="conv.id"
                class="border-b border-n-weak last:border-b-0 hover:bg-n-alpha-1 transition-colors cursor-pointer"
                :class="{
                  'ia-row': isIaRow(conv),
                }"
                @click="openSlideOver(conv.id)"
              >
                <!-- ID -->
                <td class="px-3 py-3">
                  <span
                    class="font-medium tabular-nums"
                    :class="
                      isIaRow(conv) ? 'text-purple-400' : 'text-n-blue-11'
                    "
                  >
                    #{{ conv.display_id }}
                  </span>
                </td>

                <!-- GK-HUB: Channel badge (colored pill with BrandedChannelSvg) -->
                <td class="px-3 py-3">
                  <span
                    class="text-[10px] font-medium px-2 py-0.5 rounded-full inline-flex items-center gap-1 whitespace-nowrap"
                    :class="channelBadgeClasses(conv.channel_type)"
                  >
                    <BrandedChannelSvg
                      v-if="channelBrandedName(conv.channel_type)"
                      :channel="channelBrandedName(conv.channel_type)"
                      size-class="w-3 h-3"
                    />
                    {{ conv.channel_name || '-' }}
                  </span>
                </td>
                <!-- GK-HUB: end channel badge -->

                <!-- Client -->
                <td class="px-3 py-3">
                  <span
                    class="text-n-slate-12 font-medium truncate max-w-[140px] inline-block"
                  >
                    {{ conv.contact_name || '-' }}
                  </span>
                </td>

                <!-- Agent -->
                <td class="px-3 py-3">
                  <div class="inline-flex items-center gap-1.5 min-w-0">
                    <span
                      class="font-medium truncate max-w-[120px]"
                      :class="
                        isIaRow(conv) ? 'text-purple-400' : 'text-n-slate-11'
                      "
                    >
                      {{ conv.agent_name || 'GK.IA' }}
                    </span>
                    <!-- GK-HUB: IA badge next to agent name -->
                    <span
                      v-if="conv.is_ia_agent"
                      class="gk-ia-badge flex-shrink-0"
                    >
                      {{ $t('CONVERSATION.IA_BADGE') }}
                    </span>
                  </div>
                </td>

                <!-- Motivo -->
                <td class="px-3 py-3 text-n-slate-11">
                  {{ conv.motivo || '-' }}
                </td>

                <!-- GK-HUB: Labels with colors from DB (dot + tinted background) -->
                <td class="px-3 py-3">
                  <div class="flex flex-wrap gap-1 max-w-[160px]">
                    <span
                      v-for="label in (conv.labels || []).slice(0, 3)"
                      :key="label.name || label"
                      class="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full whitespace-nowrap border"
                      :style="labelPillStyle(label)"
                    >
                      <span
                        class="w-1.5 h-1.5 rounded-full"
                        :style="{ backgroundColor: labelColor(label) }"
                      />
                      {{ labelName(label) }}
                    </span>
                    <span
                      v-if="(conv.labels || []).length > 3"
                      class="inline-block text-[10px] px-1.5 py-0.5 text-n-slate-9"
                    >
                      +{{ conv.labels.length - 3 }}
                    </span>
                  </div>
                </td>
                <!-- GK-HUB: end labels with colors -->

                <!-- TMA -->
                <td
                  class="px-3 py-3 text-right tabular-nums whitespace-nowrap"
                  :class="isIaRow(conv) ? 'text-purple-400' : 'text-n-slate-11'"
                >
                  {{
                    conv.duration_seconds
                      ? formatTime(conv.duration_seconds)
                      : '-'
                  }}
                </td>

                <!-- CSAT -->
                <td
                  class="px-3 py-3 text-center text-amber-500 whitespace-nowrap"
                >
                  {{ csatStars(conv.csat_rating) }}
                </td>

                <!-- Status -->
                <td class="px-3 py-3 text-center">
                  <span
                    v-if="isIaRow(conv)"
                    class="inline-block text-[11px] font-medium px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-400"
                  >
                    {{ t('ISP_REPORTS.CONVERSAS.STATUS_IA_RESOLVED') }}
                  </span>
                  <span
                    v-else
                    class="inline-block text-[11px] font-medium px-2 py-0.5 rounded-full"
                    :class="statusBadge(conv.status).class"
                  >
                    {{ statusBadge(conv.status).label }}
                  </span>
                </td>
              </tr>
            </tbody>
            <!-- eslint-enable vue/no-bare-strings-in-template -->
          </table>
        </div>

        <!-- Empty state -->
        <div
          v-if="!conversations.length && !isFetching"
          class="flex flex-col items-center justify-center py-16 gap-2"
        >
          <span class="i-lucide-message-square-off w-8 h-8 text-n-slate-7" />
          <p class="text-sm text-n-slate-9">
            {{ t('ISP_REPORTS.CONVERSAS.NO_DATA') }}
          </p>
        </div>

        <!-- Pagination + Export -->
        <div
          v-if="conversations.length"
          class="flex items-center justify-between px-4 py-3 border-t border-n-weak"
        >
          <!-- eslint-disable vue/no-bare-strings-in-template -->
          <span class="text-[12px] text-n-slate-9">
            Exibindo {{ showingFrom }} a {{ showingTo }} de
            {{ pagination.total_count }}
          </span>
          <!-- eslint-enable vue/no-bare-strings-in-template -->
          <div class="flex items-center gap-2">
            <!-- Export CSV (same pattern as IspFcr) -->
            <button
              class="btn-export border-n-slate-7 text-n-slate-10 hover:border-n-slate-9 hover:text-n-slate-12"
              @click="handleExport"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              {{ t('ISP_REPORTS.CONVERSAS.EXPORT_CSV') }}
            </button>
            <!-- Pagination buttons -->
            <div class="flex items-center gap-1 ml-3">
              <button
                class="px-2.5 py-1 text-[12px] rounded-md border border-n-weak text-n-slate-10 hover:bg-n-solid-3 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                :disabled="pagination.current_page <= 1"
                @click="goToPage(pagination.current_page - 1)"
              >
                {{ t('ISP_REPORTS.PAGINATION.PREVIOUS') }}
              </button>
              <!-- eslint-disable vue/no-bare-strings-in-template -->
              <span class="text-[12px] text-n-slate-9 px-2 tabular-nums">
                {{ pagination.current_page }} / {{ pagination.total_pages }}
              </span>
              <!-- eslint-enable vue/no-bare-strings-in-template -->
              <button
                class="px-2.5 py-1 text-[12px] rounded-md border border-n-weak text-n-slate-10 hover:bg-n-solid-3 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                :disabled="pagination.current_page >= pagination.total_pages"
                @click="goToPage(pagination.current_page + 1)"
              >
                {{ t('ISP_REPORTS.PAGINATION.NEXT') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Empty State (no data at all) -->
    <div
      v-if="!isFetching && !reportError && !data.data"
      class="flex flex-col items-center justify-center h-64 gap-2"
    >
      <h3 class="text-base font-semibold text-n-slate-12">
        {{ t('ISP_REPORTS.CONVERSAS.NO_DATA') }}
      </h3>
    </div>

    <!-- Slide-over overlay -->
    <Transition name="fade">
      <div
        v-if="selectedConversationId"
        class="fixed inset-0 bg-black/30 z-40"
        @click="closeSlideOver"
      />
    </Transition>

    <!-- GK-HUB: Redesigned slide-over panel (2 columns: chat + timeline) -->
    <Transition name="slide">
      <div
        v-if="selectedConversationId"
        class="fixed top-0 right-0 z-50 h-full w-full max-w-4xl bg-n-solid-2 border-l border-n-weak shadow-xl flex flex-col"
        @click.stop
      >
        <!-- Slide-over loading -->
        <div
          v-if="detailFetching && !detailConv"
          class="flex items-center justify-center h-64"
        >
          <woot-loading-state :message="t('ISP_REPORTS.LOADING')" />
        </div>

        <template v-if="detailConv">
          <!-- eslint-disable vue/no-bare-strings-in-template -->
          <!-- Header: protocol + contact + close -->
          <div
            class="sticky top-0 bg-n-solid-2 z-10 border-b border-n-weak px-5 py-4 flex-shrink-0"
          >
            <div class="flex items-center justify-between">
              <div>
                <span class="text-n-blue-11 font-mono text-sm"
                  >#GKC-{{ detailConv.display_id }}</span
                >
                <h2 class="text-base font-semibold text-n-slate-12">
                  {{ detailConv.contact_name || '-' }}
                </h2>
                <div
                  class="flex items-center gap-2 text-xs text-n-slate-9 mt-0.5"
                >
                  <!-- GK-HUB: Channel badge in slide-over header -->
                  <span
                    v-if="detailConv.channel_name"
                    class="text-[10px] font-medium px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                    :class="channelBadgeClasses(detailConv.channel_type)"
                  >
                    <BrandedChannelSvg
                      v-if="channelBrandedName(detailConv.channel_type)"
                      :channel="channelBrandedName(detailConv.channel_type)"
                      size-class="w-3 h-3"
                    />
                    {{ detailConv.channel_name }}
                  </span>
                  <!-- GK-HUB: end -->
                  <span>{{ formatDate(detailConv.created_at) }}</span>
                  <span
                    v-if="detailConv.agent_name"
                    class="inline-flex items-center gap-1"
                  >
                    {{ detailConv.agent_name }}
                    <!-- GK-HUB: IA badge next to agent name in detail drawer -->
                    <span v-if="detailConv.is_ia_agent" class="gk-ia-badge">
                      {{ $t('CONVERSATION.IA_BADGE') }}
                    </span>
                  </span>
                </div>
              </div>
              <!-- GK-HUB: Export PDF button -->
              <button
                class="isp-btn-pdf"
                :disabled="exportingPdf"
                :title="exportingPdf ? 'Gerando PDF...' : 'Exportar PDF'"
                @click="exportPdf"
              >
                <span v-if="exportingPdf" class="isp-btn-pdf__spinner" />
                <span v-else>↓ PDF</span>
              </button>
              <!-- GK-HUB: end Export PDF button -->
              <button
                class="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-n-solid-3 transition-colors cursor-pointer flex-shrink-0"
                @click="closeSlideOver"
              >
                <span class="i-lucide-x w-4 h-4 text-n-slate-10" />
              </button>
            </div>

            <!-- KPI row inline -->
            <div class="flex items-center gap-3 mt-3 flex-wrap">
              <div class="bg-n-solid-3 rounded-lg px-3 py-2">
                <div
                  class="text-[10px] text-n-slate-9 uppercase tracking-wider"
                >
                  {{ t('ISP_REPORTS.CONVERSAS.COL_ID') }}
                </div>
                <div class="text-sm font-bold text-n-blue-11 tabular-nums">
                  #GKC-{{ detailConv.display_id }}
                </div>
              </div>
              <div class="bg-n-solid-3 rounded-lg px-3 py-2">
                <div
                  class="text-[10px] text-n-slate-9 uppercase tracking-wider"
                >
                  {{ t('ISP_REPORTS.CONVERSAS.DETAIL_TMA') }}
                </div>
                <div class="text-sm font-bold text-n-blue-11 tabular-nums">
                  {{
                    detailConv.duration_seconds
                      ? formatTime(detailConv.duration_seconds)
                      : '-'
                  }}
                </div>
              </div>
              <div class="bg-n-solid-3 rounded-lg px-3 py-2">
                <div
                  class="text-[10px] text-n-slate-9 uppercase tracking-wider"
                >
                  {{ t('ISP_REPORTS.CONVERSAS.DETAIL_CSAT') }}
                </div>
                <div class="text-sm font-bold text-amber-500">
                  {{
                    detailConv.csat_rating
                      ? `${detailConv.csat_rating}/5 ★`
                      : '-'
                  }}
                </div>
              </div>
              <div class="bg-n-solid-3 rounded-lg px-3 py-2">
                <div
                  class="text-[10px] text-n-slate-9 uppercase tracking-wider"
                >
                  {{ t('ISP_REPORTS.CONVERSAS.COL_STATUS') }}
                </div>
                <span
                  class="inline-block text-[11px] font-medium px-2 py-0.5 rounded-full"
                  :class="statusBadge(detailConv.status).class"
                >
                  {{ statusBadge(detailConv.status).label }}
                </span>
              </div>
            </div>

            <!-- Labels/tags -->
            <!-- GK-HUB: Labels with colors in slide-over header -->
            <div
              v-if="detailConv.labels && detailConv.labels.length"
              class="flex gap-1.5 mt-2 flex-wrap"
            >
              <span
                v-for="label in detailConv.labels"
                :key="label.name || label"
                class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] border"
                :style="labelPillStyle(label)"
              >
                <span
                  class="w-1.5 h-1.5 rounded-full"
                  :style="{ backgroundColor: labelColor(label) }"
                />
                {{ labelName(label) }}
              </span>
            </div>
            <!-- GK-HUB: end labels with colors -->
          </div>

          <!-- Body: 2 columns -->
          <div class="flex flex-1 overflow-hidden">
            <!-- LEFT: Chat history (60%) -->
            <div class="w-3/5 overflow-y-auto p-5 border-r border-n-weak">
              <h3
                class="text-[11px] uppercase tracking-wider font-semibold text-n-slate-9 mb-3 flex items-center gap-1.5"
              >
                <span class="i-lucide-message-circle w-3.5 h-3.5" />
                {{ t('ISP_REPORTS.CONVERSAS.DETAIL_CHAT_HISTORY') }}
              </h3>

              <div
                v-if="!detailMessages.length"
                class="text-center text-sm text-n-slate-9 py-8"
              >
                {{ t('ISP_REPORTS.CONVERSAS.DETAIL_NO_MESSAGES') }}
              </div>
              <div v-else class="space-y-3">
                <div
                  v-for="msg in detailMessages"
                  :key="msg.id"
                  :class="
                    msg.message_type === 'incoming' ? '' : 'flex justify-end'
                  "
                >
                  <!-- GK-HUB: Special rendering for CSAT survey messages —
                       shows the question + rating options, highlights the one
                       the customer picked. content_type is the discriminator. -->
                  <div
                    v-if="msg.content_type === 'input_csat'"
                    class="w-full max-w-[85%] rounded-xl p-3 bg-purple-500/8 border border-purple-500/20"
                  >
                    <div
                      class="flex items-center gap-1.5 mb-2 text-[10px] text-purple-600 font-semibold uppercase tracking-wider"
                    >
                      <span class="i-lucide-star w-3 h-3" />
                      {{ t('ISP_REPORTS.CSAT.SURVEY_QUESTION_LABEL') }}
                    </div>
                    <div
                      v-if="msg.content"
                      class="text-[13px] text-n-slate-12 font-medium mb-3 whitespace-pre-wrap"
                    >
                      {{ msg.content }}
                    </div>
                    <div class="flex flex-wrap gap-2">
                      <span
                        v-for="opt in csatMessageOptions(msg)"
                        :key="opt.value"
                        class="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[12px] font-medium border transition-colors"
                        :class="
                          opt.selected
                            ? 'bg-green-500/15 border-green-500/40 text-green-600'
                            : 'bg-n-solid-3 border-n-weak text-n-slate-10'
                        "
                      >
                        {{ opt.title }}
                        <span
                          v-if="opt.selected"
                          class="i-lucide-check w-3 h-3"
                        />
                      </span>
                    </div>
                    <div
                      v-if="csatMessageSubmitted(msg)"
                      class="text-[10px] text-n-slate-9 mt-2"
                    >
                      {{ formatTimeOnly(msg.created_at) }} ·
                      {{ t('ISP_REPORTS.CSAT.SURVEY_ANSWERED') }}
                    </div>
                  </div>

                  <div
                    v-else
                    class="max-w-[80%] rounded-xl p-3 text-[13px]"
                    :class="bubbleStyle(msg)"
                  >
                    <!-- Sender label -->
                    <div class="flex items-center gap-1.5 mb-1 text-[10px]">
                      <span :class="msgIcon(msg)" class="w-3 h-3" />
                      <span class="font-medium" :class="msgSenderClass(msg)">{{
                        msg.sender_name
                      }}</span>
                    </div>
                    <div
                      v-if="msg.content"
                      class="whitespace-pre-wrap break-words leading-relaxed text-n-slate-11"
                    >
                      {{ msg.content }}
                    </div>
                    <!-- GK-HUB: Attachments (images, audio, video, files, location) -->
                    <div
                      v-if="msg.attachments && msg.attachments.length"
                      class="mt-2 space-y-2"
                    >
                      <template v-for="att in msg.attachments" :key="att.id">
                        <!-- Image -->
                        <a
                          v-if="att.file_type === 'image' && att.data_url"
                          :href="att.data_url"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="block"
                        >
                          <img
                            :src="att.data_url"
                            :alt="att.fallback_title || 'imagem'"
                            class="max-w-[280px] max-h-[240px] rounded-lg object-cover"
                            loading="lazy"
                          />
                        </a>
                        <!-- Audio -->
                        <audio
                          v-else-if="att.file_type === 'audio' && att.data_url"
                          controls
                          :src="att.data_url"
                          class="max-w-full h-8"
                        />
                        <!-- Video -->
                        <video
                          v-else-if="att.file_type === 'video' && att.data_url"
                          controls
                          :src="att.data_url"
                          class="max-w-[280px] max-h-[240px] rounded-lg"
                        />
                        <!-- Location -->
                        <a
                          v-else-if="
                            att.file_type === 'location' &&
                            att.coordinates_lat &&
                            att.coordinates_long
                          "
                          :href="`https://maps.google.com/?q=${att.coordinates_lat},${att.coordinates_long}`"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-n-alpha-2 text-n-blue-11 text-[11px]"
                        >
                          <span class="i-lucide-map-pin w-3 h-3" />
                          {{ att.fallback_title || 'Localização' }}
                        </a>
                        <!-- Generic file -->
                        <a
                          v-else-if="att.data_url"
                          :href="att.data_url"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-n-alpha-2 text-n-blue-11 text-[11px] truncate max-w-[260px]"
                        >
                          <span class="i-lucide-file w-3 h-3 flex-shrink-0" />
                          <span class="truncate">
                            {{
                              att.fallback_title || att.extension || 'arquivo'
                            }}
                          </span>
                        </a>
                      </template>
                    </div>
                    <!-- GK-HUB: end attachments -->
                    <div class="text-[10px] opacity-60 mt-1 text-right">
                      {{ formatTimeOnly(msg.created_at) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- RIGHT: Timeline (40%) -->
            <div class="w-2/5 overflow-y-auto p-5">
              <h3
                class="text-[11px] uppercase tracking-wider font-semibold text-n-slate-9 mb-3 flex items-center gap-1.5"
              >
                <span class="i-lucide-clock w-3.5 h-3.5" />
                {{ t('ISP_REPORTS.CONVERSAS.DETAIL_ACTION_TIMELINE') }}
                <span class="text-n-slate-8 font-normal ml-1">
                  {{ timeline.length }}
                  {{ t('ISP_REPORTS.CONVERSAS.DETAIL_EVENTS') }}
                  <template v-if="totalDuration">
                    · {{ formatTime(totalDuration) }}</template
                  >
                </span>
              </h3>

              <!-- Vertical timeline with dots -->
              <div v-if="timeline.length" class="relative">
                <div class="absolute left-3 top-0 bottom-0 w-[2px] bg-n-weak" />
                <div
                  v-for="(event, idx) in timeline"
                  :key="idx"
                  class="relative flex gap-3 mb-4"
                >
                  <!-- Dot -->
                  <div
                    class="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 z-10"
                    :class="event.dotBg"
                  >
                    <span
                      :class="[event.icon, event.iconColor]"
                      class="w-3 h-3"
                    />
                  </div>
                  <!-- Content -->
                  <div class="flex-1 min-w-0 pt-0.5">
                    <div class="flex items-center justify-between gap-2">
                      <span
                        class="text-xs font-semibold text-n-slate-12 truncate"
                        >{{ event.label }}</span
                      >
                      <span
                        class="text-[10px] text-n-slate-9 bg-n-solid-3 rounded px-1.5 py-0.5 flex-shrink-0 tabular-nums"
                      >
                        {{ event.relativeTime }}
                      </span>
                    </div>
                    <p
                      v-if="event.description"
                      class="text-[11px] text-n-slate-9 mt-0.5 leading-snug"
                    >
                      {{ event.description }}
                    </p>
                  </div>
                </div>
              </div>
              <div v-else class="text-center text-sm text-n-slate-9 py-8">
                {{ t('ISP_REPORTS.CONVERSAS.DETAIL_NO_MESSAGES') }}
              </div>
            </div>
          </div>
          <!-- eslint-enable vue/no-bare-strings-in-template -->

          <!-- Open full conversation button -->
          <div
            class="px-5 py-3 border-t border-n-weak bg-n-solid-2 flex-shrink-0"
          >
            <a
              :href="conversationUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="flex items-center justify-center gap-2 w-full py-2.5 bg-n-brand text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
            >
              {{ t('ISP_REPORTS.CONVERSAS.DETAIL_OPEN_FULL') }}
              <span class="i-lucide-external-link w-3.5 h-3.5" />
            </a>
          </div>
        </template>
      </div>
    </Transition>
    <!-- GK-HUB: end redesigned slide-over -->
  </div>
</template>

<style scoped>
/* ── Filter select wrapper (ensures arrow is always visible) ──────── */
.isp-select-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
}
.isp-select-arrow {
  position: absolute;
  right: 9px;
  top: 50%;
  transform: translateY(-50%);
  width: 14px;
  height: 14px;
  pointer-events: none;
  color: #9ca3af;
  flex-shrink: 0;
}

/* ── Filter select: base (light mode default) ─────────────────────── */
.isp-select {
  appearance: none !important;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  width: 100%;
  height: auto;
  margin: 0;
  font-size: 12px;
  font-family: inherit;
  line-height: 1.5;
  padding: 6px 30px 6px 10px;
  border-radius: 8px;
  outline: none;
  cursor: pointer;
  transition:
    border-color 150ms ease,
    background-color 150ms ease;
  background: none;
  /* Light mode colors */
  background-color: #f8f9fb;
  color: #4b5563;
  border: 1px solid #e0e2e6;
}
.isp-select:hover {
  border-color: #cbd0d6;
}
.isp-select:focus {
  border-color: #2e86c1;
}

/* ── Export button (same pattern as IspFcr) ──────────────────────── */
.btn-export {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border-width: 1.5px;
  border-style: solid;
  padding: 6px 16px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 150ms ease;
}

/* GK-HUB: PDF export button in slide-over header */
.isp-btn-pdf {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 32px;
  padding: 0 12px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  border: 1.5px solid #2e86c1;
  color: #2e86c1;
  background: transparent;
  transition: all 150ms ease;
  flex-shrink: 0;
}
.isp-btn-pdf:hover:not(:disabled) {
  background: rgba(46, 134, 193, 0.1);
}
.isp-btn-pdf:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.isp-btn-pdf__spinner {
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
/* GK-HUB: end PDF export button */

/* ── Chat bubbles: WhatsApp-like style ────────────────────────────── */
.chat-bubble {
  border: 1px solid transparent;
}
.chat-bubble--client {
  background-color: rgba(45, 212, 191, 0.08);
  border-color: rgba(45, 212, 191, 0.15);
  border-radius: 12px 12px 12px 4px;
}
.chat-bubble--agent {
  background-color: rgba(46, 134, 193, 0.12);
  border-color: rgba(46, 134, 193, 0.18);
  border-radius: 12px 12px 4px 12px;
}
.chat-bubble--bot {
  background-color: rgba(168, 85, 247, 0.08);
  border-color: rgba(168, 85, 247, 0.15);
  border-radius: 12px 12px 12px 4px;
}
.chat-bubble--private {
  background-color: rgba(245, 158, 11, 0.08);
  border-color: rgba(245, 158, 11, 0.15);
  border-style: dashed;
  border-radius: 12px;
}

/* ── IA row highlight ─────────────────────────────────────────────── */
.ia-row {
  background: linear-gradient(to right, rgba(168, 85, 247, 0.07), transparent);
}
.ia-row:hover {
  background: linear-gradient(
    to right,
    rgba(168, 85, 247, 0.12),
    rgba(255, 255, 255, 0.02)
  );
}

/* ── Slide-over transitions ───────────────────────────────────────── */
.slide-enter-active {
  transition: transform 300ms ease-out;
}
.slide-leave-active {
  transition: transform 200ms ease-in;
}
.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}

/* ── Overlay fade ─────────────────────────────────────────────────── */
.fade-enter-active {
  transition: opacity 300ms ease-out;
}
.fade-leave-active {
  transition: opacity 200ms ease-in;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* ── DatePicker z-index fix ───────────────────────────────────────── */
:deep(.date-picker) {
  z-index: 20;
}
</style>

<!-- Dark mode overrides (unscoped for reliable body.dark targeting) -->
<style>
/* ── Filter select: dark mode ─────────────────────────────────────── */
body.dark .isp-select {
  background-color: transparent;
  color: rgba(255, 255, 255, 0.72);
  border-color: rgba(255, 255, 255, 0.08);
}
body.dark .isp-select:hover {
  border-color: rgba(255, 255, 255, 0.14);
  background-color: rgba(255, 255, 255, 0.03);
}
/* ── Arrow icon: dark mode ────────────────────────────────────────── */
body.dark .isp-select-arrow {
  color: #64748b;
}

/* ── Chat bubbles: dark mode ──────────────────────────────────────── */
body.dark .chat-bubble--client {
  background-color: rgba(45, 212, 191, 0.08);
  border-color: rgba(45, 212, 191, 0.12);
}
body.dark .chat-bubble--agent {
  background-color: rgba(46, 134, 193, 0.1);
  border-color: rgba(46, 134, 193, 0.15);
}
body.dark .chat-bubble--bot {
  background-color: rgba(168, 85, 247, 0.07);
  border-color: rgba(168, 85, 247, 0.12);
}
body.dark .chat-bubble--private {
  background-color: rgba(245, 158, 11, 0.07);
  border-color: rgba(245, 158, 11, 0.12);
}
</style>
